import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, onSnapshot,
  doc, setDoc, deleteDoc, getDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

// ─────────────────────────────────────────────────────────────────────────────
// useFavoriteProducts
//
// Reads from userFavoriteProducts collection — mirrors iOS ProductRepository:
//   addToFavorites:    sets userFavoriteProducts/{userId}_{productId}
//   removeFromFavorites: deletes userFavoriteProducts/{userId}_{productId}
//   getFavoriteProducts: queries where userId == uid, fetches each product doc
//
// Returns real-time list of full product objects (not just IDs).
// ─────────────────────────────────────────────────────────────────────────────

const favDocId = (userId, productId) => `${userId}_${productId}`;

export const useFavoriteProducts = () => {
  const { user } = useAuth();
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [favoriteIds,      setFavoriteIds]      = useState(new Set());
  const [loading,          setLoading]          = useState(true);

  // ── Real-time listener on favorite ID docs ────────────────────────────────

  useEffect(() => {
    if (!user) {
      setFavoriteProducts([]);
      setFavoriteIds(new Set());
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'userFavoriteProducts'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q,
      async (snapshot) => {
        const ids = snapshot.docs.map(d => d.data().productId).filter(Boolean);
        setFavoriteIds(new Set(ids));

        // Fetch full product docs for each favorited ID
        try {
          const productDocs = await Promise.all(
            ids.map(id => getDoc(doc(db, 'products', id)))
          );
          const products = productDocs
            .filter(d => d.exists())
            .map(d => ({ id: d.id, ...d.data() }));
          setFavoriteProducts(products);
        } catch (err) {
          console.error('useFavoriteProducts fetch:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('useFavoriteProducts listener:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ── Toggle favorite ───────────────────────────────────────────────────────

  const toggleFavorite = useCallback(async (product) => {
    if (!user || !product?.id) return;
    const ref = doc(db, 'userFavoriteProducts', favDocId(user.uid, product.id));
    try {
      if (favoriteIds.has(product.id)) {
        await deleteDoc(ref);
      } else {
        await setDoc(ref, {
          userId:    user.uid,
          productId: product.id,
          addedAt:   serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('toggleFavorite:', err);
    }
  }, [user, favoriteIds]);

  const isFavorited = useCallback((productId) => favoriteIds.has(productId), [favoriteIds]);

  return {
    favoriteProducts,
    favoriteIds,
    loading,
    toggleFavorite,
    isFavorited,
  };
};
